import type { Gang } from "../../engine/Gang.ts";
import * as fmt from "../fmt.ts";
import { CheatModeNote } from "../PremiumContentNote/PremiumContentNote.tsx";
import { CapsOverviewBanner } from "./CapsOverviewBanner.tsx";
import { useGangContext } from "./useGang.ts";

export function StashPreviewBanner(props: {
  currentStash: number | null;
  nextStash: number | null;
}) {
  const { currentStash, nextStash } = props;

  if (currentStash === null || nextStash === null) {
    return null;
  }

  const diff = nextStash - currentStash;
  return (
    <CapsOverviewBanner
      cost={currentStash}
      label="Stash"
      additionalInfo="caps"
      isInvalid={nextStash < 0}
      diff={diff}
    >
      {currentStash === nextStash
        ? currentStash < 0
          ? `You are ${fmt.caps(Math.abs(nextStash), { context: "stash" })} in debt.`
          : `You have ${fmt.caps(nextStash, { context: "stash" })} in your stash to spend.`
        : nextStash < 0
          ? `Blimey! You will be ${fmt.caps(Math.abs(nextStash), { context: "stash" })} in debt on save.`
          : `You will have ${fmt.caps(nextStash, { context: "stash" })} left in your stash on save.`}
    </CapsOverviewBanner>
  );
}

export function PreviewBanner(props: { draftGang: Gang; where: string }) {
  const { draftGang, where } = props;
  const { gang } = useGangContext();
  const { mode, ruleset } = gang;

  if (mode.isCheatModeActive) {
    return <CheatModeNote />;
  }

  if (mode.isCampaignMode) {
    return (
      <StashPreviewBanner
        currentStash={gang.stash}
        nextStash={draftGang.stash}
      />
    );
  }

  if (mode.isManualBuildMode) {
    const maxCost = ruleset.constants.aLaCarteCapsBudget;
    const cost = gang.cost;
    const draftCost = draftGang.cost;
    const diff = cost - draftCost;

    return (
      <StashPreviewBanner
        currentStash={maxCost - cost}
        nextStash={maxCost - draftCost}
      />
    );

    return (
      <CapsOverviewBanner
        cost={maxCost - cost}
        label="Stash"
        additionalInfo="caps"
        isInvalid={draftCost > maxCost}
        diff={diff}
      >
        {cost === draftCost
          ? maxCost - cost >= 0
            ? `You can use up to ${fmt.caps(maxCost - cost, { context: "stash" })} from your stash ${where}.`
            : `You are ${fmt.caps(Math.abs(maxCost - cost), { context: "stash" })} over the limit of your stash.`
          : `You are about to use ${fmt.caps(draftCost - cost, { context: "stash" })}. ${
              maxCost - draftCost >= 0
                ? `You will have ${fmt.caps(maxCost - draftCost, { context: "stash" })} available in your stash on save.`
                : `You will be ${fmt.caps(Math.abs(maxCost - draftCost), { context: "stash" })} over the limit of your starting stash on save.`
            }`}
      </CapsOverviewBanner>
    );
  }

  return null;
}
